/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import { Injectable            } from '@angular/core';

import {
  Router,
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot, UrlSegment
} from '@angular/router';

import { Logger                } from '@/util/logger';
import { User                  } from '@/models/user';
import { AuthenticationService } from '@/services/authentication.service';
import { AlertService }          from '@/services/alert.service';

@Injectable({
  providedIn: 'root'
})

/**
 *
 * @class LoggedInGuard
 *
 * @brief this route guard just requires that there be a logged in user.  That is, the
 * authentication service must have a currently logged in user.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2020
 *
 */
export class LoggedInGuard implements CanActivate {

  /** standard constructor */
  constructor(
    private router: Router,
    private auth:   AuthenticationService,
    private alerts: AlertService
  ) {

  }

  /** check to see if current user is an administrator */
  isAdmin(): boolean {

    return this.auth.isAdmin();
  }

  /** check if route is usable, our predicate is that the user must be logged in */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot) {

    /* first we do general checks, do we basically have a logged-in user or not? */

    const currentUser: User = this.auth.userOfSession;

    if (currentUser) {

      if (!currentUser.expiryAt) {

        this.alerts.info(`Requiring login...`, {autoClose: true});

        Logger.debug(`[Auth] Requiring login...`);

        this.router.navigate(['/login'], { queryParams: { returnUrl: state.url }});

        return false;
      }

      /* check for session expiry */

      const now: number = Math.floor(Date.now() / 1000);

      if (now >= currentUser.expiryAt) {

        this.alerts.info(`Session has expired, login required, re-directing to login page...`, {autoClose: true});

        Logger.debug(`[Auth] ** EXPIRED ** ${now} >= ${currentUser.expiryAt} Requiring login on ${state.url}, redirecting to /login...`);

        this.router.navigate(['/login'], { queryParams: { returnUrl: state.url }});

        return false;
      }

      /* if the target URL is in the administration area, then only administrators may view */

      const url: string = state.url;

      if (/administration/.test(url)) {

        /* administration area requires admin level rights */

        if (!this.auth.isAdmin()) {

          this.alerts.info(`Must be an Administrator to view that page.`, {autoClose: true});

          Logger.debug(`[Auth] denying access for non-admin ${currentUser.username} on ${state.url}`);

          this.router.navigate(['/overwatch']);

          return false;
        }
      }

      /* authorised so return true */

      return true;
    }

    /* not logged in so redirect to login page with the return url */

    this.alerts.info(`Login is required, re-directing to login page...`, {autoClose: true});

    Logger.debug(`[Auth] canActivate - Requiring login on ${state.url}, redirecting to /login...`);

    this.router.navigate(['/login'], { queryParams: { returnUrl: state.url }});

    return false;
  }
}
